import classNames from 'classnames';
import _ from 'lodash';
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Droppable} from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';
import {usePopper} from 'react-popper';

// Components
import OptionPopup from 'Components/flowChart/components/OptionPopup';
import {Icon, MessageBox} from 'Components/index';

// Hooks
import useOutsideClick from 'Hooks/useOutsideClick';

// Constants
import {configs} from 'Components/flowChart/constant';
import {TRIGGER_NODE} from '../../constant';
import {checkViolationByRules} from '../../utils';

// Utils
import {getRadioValue, random} from 'Utils';

// Styled
import {
	DropPlaceholder,
	DropSpace,
	Label,
	RadioButton,
	WrapperButton,
	WrapperIcon,
	WrapperPopup
} from 'Components/flowChart/components/AddNodeButton/index.style.ts';

const PATH = 'Components/flowChart/components/AddNodeButton/index.js';

function AddNodeButton({
	data = [],
	use,
	allowAddNode = true,
	tableNodes,
	nodeData,
	droppableStyle = {},
	rules = [],
	draggingNode,
	nodeId,
	parentId = null,
	callback,
	// getTranslateMessage,
	onError = (error) => {
		console.error(error);
	},
	isViewMode,
	containerSelector = 'body'
}) {
	const arrButtonRadios = [
		{
			option: 'yes',
			label: 'Yes'
		},
		{
			option: 'no',
			label: 'No'
		}
	];
	const {value, onChange} = getRadioValue();
	const buttonNodeRef = useRef();
	const popupRef = useRef(null);
	const [messageBox, setMessageBox] = useState({
		isOpen: false,
		title: '',
		btnCloseName: 'Cancel',
		btnConfirmName: 'Save'
	});
	const [isOpen, setIsOpen] = useState(false);
	// Popper
	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const {
		styles: stylesPopper,
		attributes,
		forceUpdate
	} = usePopper(referenceElement, popperElement, {
		placement: 'bottom-start',
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 10]
				}
			}
		]
	});
	const noData = data && data.length ? false : true;

	useEffect(() => {
		try {
			if (forceUpdate) {
				forceUpdate();
			}
		} catch (e) {
			onError(e, {
				component: PATH,
				action: 'useEffect -> forceUpdate',
				args: {}
			});
		}
	}, [isOpen]);

	const callbackOptionPopup = useCallback((value) => {
		try {
			let param = {};

			const randomId = random(5);

			if (nodeId) {
				param = {
					nodeId: randomId,
					parentId: parentId,
					active: true,
					...value
				};
			}
			callback(param);
			onCloseOptionPopup();
		} catch (e) {
			onError(e, {
				component: PATH,
				action: 'callbackOptionPopup',
				args: {value}
			});
		}
	});

	const handleConfirmMessageBox = (newState) => {
		try {
			if (newState && newState.id) {
				switch (newState.id) {
					case 'ADD_NODE_IF_THEN':
						const randomNodeNo = random(4);
						const randomNodeYes = random(5);
						const parentNodeIfThen = random(6);
						let resultOption = {};

						if (value === 'yes') {
							resultOption = {
								nodeId: randomNodeYes,
								parentId: nodeId
							};
						} else {
							resultOption = {
								nodeId: randomNodeNo,
								parentId: nodeId
							};
						}
						callback(
							[
								{
									label: 'Yes',
									icon: '',
									nodeId: randomNodeYes,
									parentId: parentNodeIfThen,
									active: false,
									type: configs.NODE_TYPE_IF_THEN
								},
								{
									label: 'No',
									icon: '',
									nodeId: randomNodeNo,
									parentId: parentNodeIfThen,
									active: false,
									type: configs.NODE_TYPE_IF_THEN
								},
								{
									label: 'If/then branch',
									icon: 'icon-antsomi-diagram',
									nodeId: parentNodeIfThen,
									parentId: parentId,
									active: true,
									type: configs.NODE_TYPE_IF_THEN
								}
							],
							resultOption
						);
						onCloseMessageBox();
						break;

					default:
						break;
				}
			}
		} catch (e) {
			onError(e, {
				component: PATH,
				action: 'handleConfirmMessageBox',
				args: {newState}
			});
		}
	};

	const onCloseMessageBox = () => {
		setMessageBox({
			id: '',
			isOpen: false
		});
	};

	const onClickAddBranch = () => {
		try {
			setIsOpen(!isOpen);
		} catch (e) {
			onError(e, {
				component: PATH,
				action: 'onClickAddBranch',
				args: {}
			});
		}
	};

	const onCloseOptionPopup = () => {
		try {
			setIsOpen(false);
		} catch (e) {
			onError(e, {
				component: PATH,
				action: 'onCloseOptionPopup',
				args: {}
			});
		}
	};

	const isDroppable = useMemo(() => {
		if (!draggingNode) {
			return false;
		}

		const draggingNoData = noData && !TRIGGER_NODE.includes(draggingNode.draggableId);

		if (draggingNoData) {
			return false;
		}

		const mapNodeData = _.keyBy(nodeData, 'key');
		const draggingNodeData = mapNodeData[draggingNode.draggableId];

		if (draggingNodeData) {
			const checkViolateFn = checkViolationByRules({
				rules,
				data,
				nodeBefore: {id: nodeId}
			});

			return !checkViolateFn(draggingNodeData);
		}
	}, [draggingNode, nodeId, noData, nodeData]);

	useOutsideClick(popupRef, () => onCloseOptionPopup());

	try {
		return (
			<div className={classNames('btn-add-wrapper', {'no-data': noData})} ref={buttonNodeRef}>
				<MessageBox {...messageBox} width={530} onConfirm={handleConfirmMessageBox} onClose={onCloseMessageBox}>
					{arrButtonRadios.map((item) => (
						<Label key={item.option} onChange={onChange}>
							<RadioButton type="radio" value={item.option} readOnly checked={value == item.option} />
							{item.label}
						</Label>
					))}
				</MessageBox>
				<WrapperButton isViewMode={isViewMode} noData={noData} isDroppable={isDroppable}>
					{use === 'design' && allowAddNode === true && (
						<>
							<button
								className={classNames('btn-add', {'no-data': noData})}
								onClick={() => onClickAddBranch()}
								ref={setReferenceElement}
							>
								<WrapperIcon className="wrapper-btn-add">
									<Icon type="icon-antsomi-plus" />
								</WrapperIcon>
							</button>
							<Droppable droppableId={nodeId}>
								{(provided) => (
									<DropSpace
										ref={provided.innerRef}
										{...provided.droppableProps}
										style={{...droppableStyle}}
										className="drop-space"
									>
										<DropPlaceholder>{provided.placeholder}</DropPlaceholder>
									</DropSpace>
								)}
							</Droppable>
						</>
					)}
				</WrapperButton>

				{isOpen &&
					ReactDOM.createPortal(
						<WrapperPopup
							ref={setPopperElement}
							style={{...stylesPopper.popper, zIndex: 1000}}
							{...attributes.popper}
						>
							<OptionPopup
								rules={rules}
								ref={popupRef}
								tableNodes={tableNodes}
								dataNodes={data}
								isOpen={isOpen}
								callback={callbackOptionPopup}
								nodeId={nodeId}
							/>
						</WrapperPopup>,
						document.querySelector(containerSelector)
					)}
			</div>
		);
	} catch (e) {
		onError(e, {
			component: PATH,
			action: 'render -> AddNodeButton',
			args: {}
		});
	}
}

export default memo(AddNodeButton);
